/**
 * Where a scanned Web QR code lands, and how that link is built.
 *
 * The host is picked per code when it is generated and stored on the code as
 * `base_url`. Older codes predate that column, so for them the choice lives
 * only in this browser's localStorage, and the dashboard composes the link.
 *
 * These values live here rather than inside one component because two screens
 * now render the same QR: the Web QR management page and the campaign detail
 * page's QR Instance table. A second copy of the default host would mean a
 * silently wrong (dead) QR on one of them the day it changes.
 */

export interface QrDomainOption {
  label: string;
  base: string;
}

/**
 * The Luqta web app, used unless the client picked one of their own domains
 * (registered under Branding → Domains & Hosts).
 */
export const DEFAULT_QR_DOMAIN: QrDomainOption = {
  label: 'Luqta Web',
  base: 'https://web-luqta.vercel.app/q',
};

export const DEFAULT_QR_BASE = DEFAULT_QR_DOMAIN.base;

/** Path the public web app serves scanned codes from, appended to a branded host. */
export const QR_DOMAIN_PATH = '/q';

/** localStorage key prefix holding the per-code host choice, keyed by campaign. */
export const QR_BASE_STORAGE_PREFIX = 'webQrBase:';

export function composeQrShortLink(slug: string, base: string): string {
  const cleanBase = String(base || '').replace(/\/+$/, '');
  const cleanSlug = String(slug || '').replace(/^\/+/, '');
  return cleanBase && cleanSlug ? `${cleanBase}/${cleanSlug}` : '';
}

/** The slug → host map saved for one campaign, or {} when there is none. */
export function readQrBaseOverrides(contestId: number | string): Record<string, string> {
  try {
    const raw = localStorage.getItem(`${QR_BASE_STORAGE_PREFIX}${contestId}`);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Blocked or full storage costs the choice its persistence, nothing more.
    return {};
  }
}

/**
 * The URL a given instance's QR should encode.
 *
 * A host chosen for this code (its saved `base_url`, else this browser's
 * remembered pick) wins over any `short_link` the API sends: the backend may
 * compose that field on the default host, which would point the QR elsewhere.
 *
 * Accepts rows from either shape — qr_instance_list (which has `short_link`)
 * or qr_report_detail's instances (which have only `slug`).
 */
export function resolveQrInstanceLink(instance: any, contestId: number | string): string {
  const slug = String(instance?.slug || '').trim();
  // The domain saved on the code itself wins; the per-browser localStorage
  // choice only covers codes made before the backend stored one.
  const chosenBase = instance?.base_url || (slug ? readQrBaseOverrides(contestId)[slug] : '');

  if (chosenBase) {
    return composeQrShortLink(slug, chosenBase);
  }

  return (
    instance?.qr_value ||
    instance?.short_link ||
    instance?.link ||
    instance?.url ||
    composeQrShortLink(slug, DEFAULT_QR_BASE)
  );
}
